import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, MapPin, Clock } from "lucide-react";
import SEOHead from "../components/SEOHead";
import SchemaJsonLd from "../components/SchemaJsonLd";
import MapWidget from "../components/MapWidget";
import { getCity } from "../lib/api";
import { localBusinessSchema, breadcrumbSchema, webPageSchema } from "../lib/seo";

export default function CityPage({ brand }) {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        getCity(slug)
            .then((d) => !cancelled && setData(d))
            .catch(() => !cancelled && setError("not found"));
        return () => {
            cancelled = true;
        };
    }, [slug]);
    const isStale = data && data.slug && data.slug !== slug;

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <SEOHead
                    title="Area not listed yet"
                    description="This area isn't in our directory yet. Browse all 80+ NY neighborhoods served by ZenZest."
                    canonical={`https://weeddispensarynearme.com/dispensary-near/${slug}`}
                />
                <h1 className="font-serif text-4xl font-light mb-6 text-zen-ink">
                    We don&rsquo;t list that area yet.
                </h1>
                <Link to="/sitemap" className="text-zen-olive underline">
                    Browse all 80+ neighborhoods
                </Link>
            </div>
        );
    }
    if (!data || isStale) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-zen-ink-soft text-center">
                <SEOHead
                    title="Loading area…"
                    description="Loading the latest neighborhood and ZenZest dispensary details."
                    canonical={`https://weeddispensarynearme.com/dispensary-near/${slug}`}
                />
                Loading…
            </div>
        );
    }

    const { city, zip, state, minutes_to_store, store } = data;
    const canonical = `https://weeddispensarynearme.com/dispensary-near/${slug}`;

    return (
        <>
            <SEOHead
                title={`Weed Dispensary Near ${city}, ${state} ${zip}`}
                description={`Looking for a legal weed dispensary near ${city}, ${state}? ZenZest ${store.short_name} (${store.street}) is just ${minutes_to_store} min away. NY OCM-licensed. Free same-day delivery on $100+.`}
                canonical={canonical}
                image={store.image}
            />
            <SchemaJsonLd
                schema={[
                    webPageSchema({
                        url: canonical,
                        name: `Weed Dispensary Near ${city}, ${state} ${zip}`,
                    }),
                    localBusinessSchema(store, brand),
                    breadcrumbSchema([
                        { name: "Home", url: "https://weeddispensarynearme.com/" },
                        {
                            name: "Dispensary Near",
                            url: "https://weeddispensarynearme.com/dispensary-near",
                        },
                        { name: city, url: canonical },
                    ]),
                ]}
            />

            {/* HERO (dark) */}
            <section
                className="relative overflow-hidden bg-zen-elevated"
                style={{
                    backgroundImage: `linear-gradient(180deg, rgba(40,48,31,0.5), rgba(40,48,31,0.92)), url('${store.image}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-24 pb-20 sm:pt-32 sm:pb-32">
                    <nav className="text-[11px] uppercase tracking-[0.25em] text-zen-cream-muted mb-8">
                        <Link to="/" className="hover:text-white">
                            Home
                        </Link>{" "}
                        ·{" "}
                        <Link to="/sitemap" className="hover:text-white">
                            Cities
                        </Link>{" "}
                        ·{" "}
                        <span className="text-zen-olive-light">{city}</span>
                    </nav>
                    <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive-light mb-6 flex items-center gap-3">
                        <span className="w-8 h-px bg-zen-olive-light" />
                        Weed dispensary near {city} {state} · {zip}
                    </p>
                    <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-light text-white leading-[0.95] tracking-tight max-w-4xl">
                        Your closest legal weed dispensary in{" "}
                        <span className="italic text-zen-olive-light">
                            {city}.
                        </span>
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-white/85 leading-relaxed">
                        {city}, {state} residents have a{" "}
                        <strong className="text-white">
                            {minutes_to_store}-minute drive
                        </strong>{" "}
                        to the nearest fully licensed adult-use dispensary:{" "}
                        <strong className="text-white">{store.name}</strong>.
                        NY OCM-licensed, family-owned, 4.9★ from 2,600+ verified
                        reviews.
                    </p>
                    <div className="mt-12 flex flex-wrap gap-3">
                        <Link
                            to={`/locations/${store.slug}`}
                            className="inline-flex items-center gap-2 bg-zen-olive hover:bg-zen-olive-dark text-zen-bg px-8 py-4 font-medium uppercase tracking-[0.25em] text-[11px] transition-colors"
                        >
                            See store details <ArrowUpRight size={14} />
                        </Link>
                        <a
                            href={store.menu_url || store.store_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 border border-white/40 hover:border-white hover:bg-white hover:text-zen-ink text-white px-8 py-4 font-medium uppercase tracking-[0.25em] text-[11px] transition-all"
                        >
                            Shop the menu
                        </a>
                    </div>
                </div>
            </section>

            {/* BODY (light) */}
            <section className="bg-zen-bg max-w-[1400px] mx-auto px-6 sm:px-10 py-14">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <article className="lg:col-span-7">
                        <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-4">
                            About ZenZest near {city}
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-zen-ink leading-[1.05] mb-6">
                            Why {city} chooses {store.short_name}.
                        </h2>
                        <div className="space-y-6 text-zen-ink-soft leading-relaxed text-base">
                            <p>{store.pitch}</p>
                            <p>
                                If you&rsquo;re in <strong className="text-zen-ink">{city}</strong>{" "}
                                (ZIP {zip}) and you&rsquo;ve been searching{" "}
                                <em>&ldquo;weed dispensary near me&rdquo;</em>,
                                ZenZest {store.short_name} is the closest fully
                                licensed, OCM-regulated adult-use cannabis store
                                — just {minutes_to_store} minutes away at{" "}
                                {store.street}.
                            </p>
                            <p>
                                Every product on our shelves is third-party
                                lab-tested for potency, purity, and
                                pesticide-free quality. We stock 109+ rotating
                                cultivars across flower, edibles, vapes,
                                concentrates, pre-rolls, and accessories — one
                                of the largest legal selections in New York
                                State.
                            </p>
                        </div>

                        <h3 className="font-serif text-2xl font-light mt-12 mb-5 text-zen-ink">
                            What to expect when you visit
                        </h3>
                        <ul className="space-y-3 text-zen-ink-soft">
                            {store.highlights.map((h, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="mt-2 h-1.5 w-1.5 bg-zen-olive rounded-full shrink-0" />
                                    <span>{h}</span>
                                </li>
                            ))}
                            <li className="flex items-start gap-3">
                                <span className="mt-2 h-1.5 w-1.5 bg-zen-olive rounded-full shrink-0" />
                                <span>
                                    Free same-day delivery to {city} on orders
                                    over $100.
                                </span>
                            </li>
                        </ul>

                        <h3 className="font-serif text-2xl font-light mt-12 mb-5 text-zen-ink">
                            Getting here from {city}
                        </h3>
                        <p className="text-zen-ink-soft leading-relaxed">
                            From {city} ({zip}), it&rsquo;s roughly{" "}
                            {minutes_to_store} minutes by car to {store.street},{" "}
                            {store.city}, {store.state} {store.zip}.{" "}
                            <a
                                href={`https://maps.google.com/?q=${encodeURIComponent(store.street + ", " + store.city + ", " + store.state + " " + store.zip)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-zen-olive border-b border-zen-olive/40 hover:border-zen-olive-dark hover:text-zen-olive-dark"
                            >
                                Open in Google Maps →
                            </a>
                        </p>
                    </article>

                    <aside className="lg:col-span-5 bg-zen-surface border border-zen-ink/10 p-8 h-fit">
                        <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-4">
                            Your nearest ZenZest
                        </p>
                        <p className="font-serif text-2xl text-zen-ink font-light leading-tight mb-6">
                            {store.name}
                        </p>
                        <div className="text-sm space-y-3 text-zen-ink mb-7">
                            <div className="flex items-start gap-3">
                                <MapPin size={14} className="text-zen-olive mt-1" />
                                <span>
                                    {store.street}
                                    <br />
                                    {store.city}, {store.state} {store.zip}
                                </span>
                            </div>
                            <div className="flex items-center gap-3">
                                <Clock size={14} className="text-zen-olive" />
                                <span>~{minutes_to_store} min from {city}</span>
                            </div>
                        </div>
                        <MapWidget
                            locations={[store]}
                            center={[store.lat, store.lng]}
                            zoom={12}
                            height={280}
                        />
                        <Link
                            to={`/locations/${store.slug}`}
                            className="block text-center mt-6 bg-zen-olive hover:bg-zen-olive-dark text-zen-bg py-3.5 font-medium uppercase tracking-[0.22em] text-[11px] transition-colors"
                        >
                            Full store details
                        </Link>
                    </aside>
                </div>
            </section>
        </>
    );
}
